/**
 * Resolution Context — pure resolver for the Unified Resolution Drawer.
 * ─────────────────────────────────────────────────────────────────────
 * Aggregates a read-only snapshot of everything an operator needs to
 * triage an execution issue for a single job: customer/address/installer,
 * current phase, lifecycle hints, related issue context, deep-link
 * actions.
 *
 * Hard rules:
 *  - Pure function. No React.
 *  - No financial fields (contractValue, payoutAmount, prices) ever.
 *  - No customer PII beyond name + address (already surfaced in
 *    Install Today and Job Detail). Email/phone deliberately omitted.
 *  - Reuses canonical sources only — never re-computes truth.
 */
import type { Job, Issue } from "@/data/types";
import type { ServiceTicket } from "@/data/serviceTicketTypes";
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "@/data/contractor/networkExecution/holdback/ledger";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import type {
  FieldMaterialFeedbackEvent,
  SubstitutionRequest,
} from "@/data/VendorPOContext";
import type { JobSpecExecution } from "@/data/jobSpecExecution";
import type { Contractor } from "@/data/types";
import { resolvePermitOperationalSignals } from "@/data/permitting/permitOperationalSignals";
import type {
  ResolutionDrawerSource,
  ResolutionDrawerEventContext,
} from "./resolutionDrawerBus";

export interface ResolutionDeepLink {
  id:
    | "open_job"
    | "open_qa"
    | "open_issues"
    | "open_scheduling"
    | "open_checklist"
    | "open_callbacks"
    | "open_holdbacks";
  label: string;
  href: string;
  hint: string;
}

export interface ResolutionContext {
   jobId: string;
   jobNumber: string;
   source: ResolutionDrawerSource;
   eventContext?: ResolutionDrawerEventContext;
   jobFound: boolean;

   // Summary block
   customerName: string;
   address: string;
   installerName: string;
   currentPhase: string;
   lifecycle: string;

  // Related context
  related: {
    callbackCount: number;
    openIssueCount: number;
    openTicketCount: number;
    activeHoldbackCount: number;
    unresolvedFieldFeedbackCount: number;
    openSubstitutionCount: number;
    qaStatus: "awaiting" | "failed" | "passed" | "in_progress" | "unavailable";
    permitStatus: string;
    materialReadiness: string;
    dispatchStatus: string;
    readinessLocked: boolean;
  };

  // Deep links
  deepLinks: ResolutionDeepLink[];
}

export interface BuildResolutionContextInput {
  jobId: string;
  source: ResolutionDrawerSource;
  eventContext?: ResolutionDrawerEventContext;
  jobs: Job[];
  contractors: Contractor[];
  callbacks: Callback[];
  holdbackLedger: HoldbackLedgerEntry[];
  issues: Issue[];
  serviceTickets: ServiceTicket[];
  fieldMaterialFeedback: FieldMaterialFeedbackEvent[];
  substitutionRequests: SubstitutionRequest[];
  specExecutions: JobSpecExecution[];
}

function formatAddress(job: Job): string {
  const a = job.address;
  if (!a) return "—";
  const parts = [a.street, a.city, a.state].filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : "—";
}

function resolveInstallerName(
  job: Job,
  contractors: Contractor[],
): string {
  const id = job.assignedSubcontractorId ?? job.ownerContractorId;
  if (id) {
    const c = contractors.find((x) => x.id === id);
    if (c) return c.companyName;
  }
  return job.assignedSubcontractor || "Unassigned";
}

function resolveQaStatus(
  jobId: string,
  specExecutions: JobSpecExecution[],
): ResolutionContext["related"]["qaStatus"] {
  const exec = specExecutions.find((e) => e.jobId === jobId);
  if (!exec) return "unavailable";
  // Most recent phase activity dictates qa state.
  let latest: { status: string; at: string } | null = null;
  for (const p of exec.phases) {
    const at = p.completedAt ?? p.qaReviewedAt;
    if (!at) continue;
    if (!latest || at > latest.at) latest = { status: p.status, at };
  }
  if (!latest) return "unavailable";
  if (latest.status === "submitted") return "awaiting";
  if (latest.status === "qa_failed") return "failed";
  if (latest.status === "qa_passed") return "passed";
  if (latest.status === "in_progress") return "in_progress";
  return "unavailable";
}

function resolveCurrentPhase(
  jobId: string,
  specExecutions: JobSpecExecution[],
): string {
  const exec = specExecutions.find((e) => e.jobId === jobId);
  if (!exec) return "—";
  // Last non-passed phase, else last passed.
  const inFlight = exec.phases.find(
    (p) =>
      p.status === "in_progress" ||
      p.status === "submitted" ||
      p.status === "qa_failed",
  );
  if (inFlight) return inFlight.phaseName;
  const last = exec.phases[exec.phases.length - 1];
  return last?.phaseName ?? "—";
}

function lifecycleFromJob(job: Job): string {
  if (job.currentStage) return String(job.currentStage);
  return "—";
}

export function buildResolutionContext(
  input: BuildResolutionContextInput,
): ResolutionContext {
  const {
    jobId,
    source,
    eventContext,
    jobs,
    contractors,
    callbacks,
    holdbackLedger,
    issues,
    serviceTickets,
    fieldMaterialFeedback,
    substitutionRequests,
    specExecutions,
  } = input;

const job = jobs.find((j) => j.id === jobId);
   if (!job) {
     return {
       jobId,
       jobNumber: "—",
       source,
       eventContext,
       jobFound: false,
       customerName: "—",
       address: "—",
       installerName: "—",
       currentPhase: "—",
       lifecycle: "—",
      related: {
        callbackCount: 0,
        openIssueCount: 0,
        openTicketCount: 0,
        activeHoldbackCount: 0,
        unresolvedFieldFeedbackCount: 0,
        openSubstitutionCount: 0,
        qaStatus: "unavailable",
        permitStatus: "—",
        materialReadiness: "—",
        dispatchStatus: "—",
        readinessLocked: false,
      },
      deepLinks: [
        {
          id: "open_job",
          label: "Open Job",
          hint: "Open the Job Detail Command Center.",
          href: `/jobs/${jobId}`,
        },
      ],
    };
  }

  const callbackCount = callbacks.filter((c) => c.jobId === jobId).length;

  const holdbackStates = deriveAllHoldbackStates(
    holdbackLedger.filter((h) => h.jobId === jobId),
  );
  const activeHoldbackCount = holdbackStates.filter(
    (s) => s.netHeld > 0,
  ).length;

  const openIssueCount = issues.filter(
    (i) => i.jobId === jobId && i.status !== "Resolved" && i.status !== "Closed",
  ).length;
  const openTicketCount = serviceTickets.filter(
    (t) => t.jobId === jobId && t.status !== "Resolved" && t.status !== "Closed",
  ).length;

  const unresolvedFieldFeedbackCount = fieldMaterialFeedback.filter(
    (e) => e.jobId === jobId && !e.resolved,
  ).length;
  const openSubstitutionCount = substitutionRequests.filter(
    (s) =>
      s.jobId === jobId &&
      (s.status === "pending_review" || s.status === "approved"),
  ).length;

  const permit = resolvePermitOperationalSignals(job);

  const ctx: ResolutionContext = {
    jobId,
    jobNumber: job.jobNumber ?? "—",
    source,
    eventContext,
    jobFound: true,
    customerName: job.customerName ?? "—",
    address: formatAddress(job),
    installerName: resolveInstallerName(job, contractors),
    currentPhase: resolveCurrentPhase(jobId, specExecutions),
    lifecycle: lifecycleFromJob(job),
    related: {
      callbackCount,
      openIssueCount,
      openTicketCount,
      activeHoldbackCount,
      unresolvedFieldFeedbackCount,
      openSubstitutionCount,
      qaStatus: resolveQaStatus(jobId, specExecutions),
      permitStatus: permit.installBlockerReason || job.permitStatus || "—",
      materialReadiness: job.materialReadiness || "—",
      dispatchStatus: job.dispatchStatus || "—",
      readinessLocked: Boolean(
        (job as unknown as { executionReady?: { lockedAt?: string } }).executionReady?.lockedAt,
      ),
    },
    deepLinks: [
      {
        id: "open_job",
        label: "Open Job",
        hint: "Open the Job Detail Command Center.",
        href: `/jobs/${jobId}`,
      },
      {
        id: "open_qa",
        label: "Open QA",
        hint: "Open the QA form for this job.",
        href: `/qa-form?jobId=${jobId}`,
      },
      {
        id: "open_issues",
        label: "Open Issues",
        hint: "Open the Issues list filtered to this job.",
        href: `/issues?job=${jobId}`,
      },
      {
        id: "open_scheduling",
        label: "Open Scheduling",
        hint: "Open the scheduling surface for this job.",
        href: `/scheduling?job=${jobId}`,
      },
      {
        id: "open_checklist",
        label: "Open Checklist",
        hint: "Open the installer execution flow.",
        href: `/installer-execution/${jobId}`,
      },
      {
        id: "open_callbacks",
        label: "Open Callbacks",
        hint: "Open QA & Callbacks scoped to this job.",
        href: `/execution/qa-callbacks?job=${jobId}`,
      },
      {
        id: "open_holdbacks",
        label: "Open Holdbacks",
        hint: "Open the Holdbacks & Disputes surface.",
        href: `/execution/holdbacks`,
      },
    ],
  };

  return ctx;
}

/** Route prefixes any deep link can produce. Used by regression tests. */
export const RESOLUTION_DEEP_LINK_PREFIXES = [
  "/jobs/",
  "/qa-form",
  "/issues",
  "/scheduling",
  "/installer-execution/",
  "/execution/qa-callbacks",
  "/execution/holdbacks",
] as const;
